import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchUsers, adminUpdateUserRole } from '@/lib/api'
import type { AdminUser } from '@/lib/types'

export default function UsersList() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [updating, setUpdating] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError('')
    try { setUsers(await fetchUsers()) }
    catch (e: unknown) { setError(e instanceof Error ? e.message : 'Failed to load users') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  async function handleRole(userId: string, role: string) {
    setUpdating(userId)
    setError('')
    try {
      await adminUpdateUserRole(userId, role)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update role')
    }
    setUpdating(null)
  }

  return (
    <Layout title="Users">
      <div className="max-w-5xl mx-auto space-y-5">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

        <div className="bg-white rounded-2xl border border-surface-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 text-left">
                <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">
                  No users yet. Sign in once with your Google account, then run the SQL snippet to promote yourself to admin.
                </td></tr>
              ) : users.map(u => (
                <tr key={u.user_id} className="border-b border-surface-100 hover:bg-surface-50/60 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-slate-900">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${
                      u.role === 'admin' ? 'bg-brand-50 text-brand-700 border-brand-200' : 'bg-slate-100 text-slate-600 border-slate-200'
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3.5 text-right">
                    {updating === u.user_id ? (
                      <span className="text-xs text-slate-400">...</span>
                    ) : (
                      <button
                        onClick={() => handleRole(u.user_id, u.role === 'admin' ? 'owner' : 'admin')}
                        className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                      >
                        {u.role === 'admin' ? 'Demote to owner' : 'Promote to admin'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  )
}
